import React from 'react';
import { AiOutlineMinusCircle, AiOutlinePlusCircle } from 'react-icons/ai';

import { Text } from '@/components/typography/text';
import {
  AgentConfigGlobalContexts,
  AgentConfigStepTypeEnum,
  CONTEXT_INPUT_TYPES,
  ContextInputType
} from '@/types/agent';

import { getNextNewKey } from '../../util/getNextNewKey';


const ENTITY = 'context';

type Props = {
  editable?: boolean;
  globalContext: AgentConfigGlobalContexts;
  onContextsChange: ( contexts: AgentConfigGlobalContexts ) => void;
  stepType: AgentConfigStepTypeEnum;
};


export default function PaneGlobalContexts( props: Props ) {
  const {
    editable = false,
    globalContext = {},
    onContextsChange
  } = props;

  const onAddContextClick = () => {
    const newContextKey = getNextNewKey( Object.keys( globalContext ), ENTITY );

    const nextContexts = {
      ...globalContext,
      [ newContextKey ]: { type: 'STRING' as ContextInputType }
    };

    onContextsChange( nextContexts );
  };

  const onDeleteClick = ( key: string ) => {
    const { [ key ]: _, ...nextContexts } = globalContext;

    onContextsChange( nextContexts );
  };

  const onKeyChange = ( e: React.FormEvent<HTMLInputElement>, oldKey: string ) => {
    const newKey = e.currentTarget.value;

    const nextContexts: AgentConfigGlobalContexts = {};

    for ( const k of Object.keys( globalContext ) ) {
      nextContexts[ k === oldKey ? newKey : k ] = globalContext[ k ];
    }

    onContextsChange( nextContexts );
  };

  const onValueChange = ( e: React.FormEvent<HTMLSelectElement>, key: string ) => {
    const nextContexts = {
      ...globalContext,
      [ key ]: { type: e.currentTarget.value as ContextInputType }
    };

    onContextsChange( nextContexts );
  };

  const contextEntries = Object.entries( globalContext ?? {} );

  return (
    <div className='configPane'>
      <Text as='div' className='paneTitle' size='md' weight='bold'>Global Context</Text>
      {
        contextEntries.map(
          ( [ k, v ], i ) => (
            <div className='contextRow' key={ i }>
              {
                editable && (
                  <AiOutlineMinusCircle
                    className='contextIcon'
                    data-testid={ `config-edit-panel-contexts-context-delete-${ i }` }
                    onClick={ () => onDeleteClick( k ) }
                  />
                )
              }
              <div className='contextName'>
                <input
                  data-testid={ `config-edit-panel-contexts-context-${ i }-key` }
                  disabled={ !editable }
                  onInput={ e => onKeyChange( e, k ) }
                  type='text'
                  value={ k }
                />
              </div>
              <div className='contextValue'>
                <select
                  data-testid={ `config-edit-panel-contexts-context-${ i }-value` }
                  value={ v.type }
                  onChange={ e => onValueChange( e, k ) }
                  disabled={ !editable }
                >
                  {
                    ( Object.entries( CONTEXT_INPUT_TYPES ) as Array<[ ContextInputType, string ]> ).map(
                      ( [ type, label ] ) => (
                        <option key={ type } value={ type }>{ label }</option>
                      )
                    )
                  }
                </select>
              </div>
            </div>
          )
        )
      }
      {
        !contextEntries.length && (
          <div className='contextRow'>
            none
          </div>
        )
      }
      {
        editable && (
          <div
            className='addContextRow'
            onClick={ onAddContextClick }
            data-testid='config-edit-panel-contexts-context-add-button'
          >
            <AiOutlinePlusCircle className='button' />
            <Text as='span' size='sm'>Add context</Text>
          </div>
        )
      }
    </div>
  );
}
